import {
  MIME_TYPE_EXTENSIONS,
} from '@/generated/mimeTypeDb.ts';
import {
  EMimeType,
} from '@/types/mime.ts';
import {
  formatNumber,
} from '@/utils/format-value.ts';

const KILO_BYTES = 1024;
const MEGA_BYTES = 1024 * KILO_BYTES;

// bytes
// 50mb
export const MAXIMUM_FILE_SIZE = 50 * MEGA_BYTES;

// 10mb
export const MAXIMUM_IMAGE_SIZE = 10 * MEGA_BYTES;

enum EMimeGroup {
  images = 'images',
  video = 'video',
  audio = 'audio',
  documents = 'documents',
  data = 'data',
}

/**
 * This list contains support groups of mime types based on their general group
 */
export const MIME_GROUPS: Record<EMimeGroup, EMimeType[]> = {
  [EMimeGroup.images]: [
    // See https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/img#supported_image_formats
    // for a list of commonly supported image formats
    EMimeType.IMAGE_GIF,
    EMimeType.IMAGE_JPG,
    EMimeType.IMAGE_PNG,
    EMimeType.IMAGE_SVG,
    EMimeType.IMAGE_WEBP,
  ],
  [EMimeGroup.video]: [
    EMimeType.VIDEO_MOV,
    EMimeType.VIDEO_MP4,
    EMimeType.VIDEO_OGG,
    EMimeType.VIDEO_WEBM,
  ],
  [EMimeGroup.audio]: [
    EMimeType.AUDIO_AAC,
    EMimeType.AUDIO_FLAC,
    EMimeType.AUDIO_MPG,
    EMimeType.AUDIO_OGG,
    EMimeType.AUDIO_WAV,
  ],
  [EMimeGroup.documents]: [
    EMimeType.MARKDOWN,
    EMimeType.MICROSOFT_POWERPOINT,
    EMimeType.MICROSOFT_POWERPOINT_LEGACY,
    EMimeType.MICROSOFT_WORD,
    EMimeType.MICROSOFT_WORD_LEGACY,
    EMimeType.PDF,
  ],
  [EMimeGroup.data]: [
    EMimeType.CSV,
    EMimeType.JSON,
    EMimeType.MICROSOFT_EXCEL,
    EMimeType.MICROSOFT_EXCEL_LEGACY,
  ],
} as const;

export function getFileExtensions(mimeTypes: EMimeType[]): string[] {
  const fileExtensions: string[] = [];
  mimeTypes.forEach((mimeType) => {
    fileExtensions.push(...MIME_TYPE_EXTENSIONS[mimeType]);
  });
  return fileExtensions;
}

export function formatFileSize(bytes: number): string {
  if (bytes < KILO_BYTES) {
    return `${formatNumber(bytes)} B`;
  } else if (bytes < MEGA_BYTES) {
    return `${formatNumber(bytes / KILO_BYTES)} KB`;
  } else {
    return `${formatNumber(bytes / MEGA_BYTES)} MB`;
  }
}
